import dayjs from 'dayjs';
import { STOCK_QUOTE_TYPE } from '@/pages/find/stock/constants';
import { formatCustomTime } from '@/utils/api';

const TRADING_DAY_FORMAT = 'YYYY-MM-DD HH:mm:ss';

/**
 * 将接口时间字段转为毫秒时间戳。
 * @param {string|number} input
 * @returns {number|null}
 */
export function toKlineTimestamp(input) {
  if (typeof input === 'number' && Number.isFinite(input)) {
    return input;
  }
  const ts = dayjs(input).valueOf();
  return Number.isFinite(ts) ? ts : null;
}

/**
 * 规范化 realtime-klineHistory 接口的 quotations 数据。
 * @param {Array} list
 * @returns {Array}
 */
export function normalizeMinuteQuotations(list, options = {}) {
  const { quoteType } = options;
  if (!Array.isArray(list)) {
    return [];
  }
  const result = list
    .map((item) => {
      return {
        ...item,
        tradingDay:
          quoteType === STOCK_QUOTE_TYPE.DAILY_K
            ? formatCustomTime(item.timeIndex, TRADING_DAY_FORMAT)
            : item.tradingDay,
        timeIndex: item.timeIndex,
        openPrice: Number(item.openPrice) || 0,
        closePrice: Number(item.closePrice) || 0,
        highPrice: Number(item.highPrice) || 0,
        lowPrice: Number(item.lowPrice) || 0,
        // 分钟接口在不同环境下前复权字段命名不一致，统一补齐并回落到不复权字段，保证前复权切换可用。
        tlFrontOpenPrice: Number(item.tlFrontOpenPrice ?? item.openPrice) || 0,
        frontAdjustPrice: Number(item.frontAdjustPrice ?? item.closePrice) || 0,
        tlFrontHighPrice: Number(item.tlFrontHighPrice ?? item.highPrice) || 0,
        tlFrontLowPrice: Number(item.tlFrontLowPrice ?? item.lowPrice) || 0,
        turnoverVolume: Number(item.turnoverVolume) || 0,
        changePct: item.changePct ?? '0.00',
        frontChangePct: item.frontChangePct ?? item.changePct ?? '0.00',
        changeQuota: item.changeQuota,
        avgPrice: item.avgPrice,
        date: dayjs(item.tradingDay).format('YYYY-MM-DD'),
        weekId: quoteType === STOCK_QUOTE_TYPE.WEEKLY_K ? item.timeIndex : undefined,
        monthId: quoteType === STOCK_QUOTE_TYPE.MONTHLY_K ? item.timeIndex : undefined
      };
    })
    .filter(Boolean);

  return result;
}

/**
 * 合并 K 线历史数据
 * @param {Array} olderList
 * @param {Array} latestList
 * @param {{ mergeMode?: 'minute'|'period' }} [options]
 * @returns {Array}
 */
export function mergeMinuteKlineData(olderList, latestList, options = {}) {
  const { mergeMode = 'minute' } = options;
  const dataMap = new Map();
  [...olderList, ...latestList].forEach((item) => {
    if (!item) {
      return;
    }
    const key = item.timeIndex;
    if (!key) {
      return;
    }
    const prev = dataMap.get(key);
    if (mergeMode === 'period') {
      dataMap.set(key, {
        ...prev,
        ...item,
        tradingDay: prev?.tradingDay ?? item.tradingDay,
        date: prev?.date ?? item?.date,
        timeIndex: String(item.timeIndex)
      });
      return;
    } else {
      dataMap.set(key, {
        ...prev,
        ...item,
        tradingDay: dayjs(item.timeIndex).format(TRADING_DAY_FORMAT)
      });
    }
  });
  return Array.from(dataMap.values());
}
